import { useState, useEffect } from 'react';
import styles from '../BusinessProfileMain.module.css';
import axios from 'axios';
import { API_URL } from '../../../utils/auth';
import Auth from '../../../utils/auth';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const initialCheckboxes = {
  budgetConstraints: false,
  projectCompletion: false,
  lackOfPerceivedValue: false,
  poorCommunication: false,
  competitorSwitching: false,
  internalChangesAtClientCompany: false,
  seasonalFactors: false,
  other: false,
};

const clientChurnTriggersOptions = [
  '',
  'Founder/Owner',
  'Dedicated account managers',
  'Project managers',
  'Service delivery team',
  'Automated systems',
  'Combination of above',
];

// Utility function to normalize option values
const normalizeOptionValue = (value, options) => {
  if (!value) return '';
  return options.find(opt => opt.toLowerCase().trim() === value.toLowerCase().trim()) || '';
};

const ClientSuccessFactors = () => {
  const [fields, setFields] = useState({
    clientChurnTriggers: '',
    uniqueValueProposition: '',
    idealClientJourney: '',
    ...initialCheckboxes,
  });
  const [id, setId] = useState(null);
  const [loading, setLoading] = useState(false);
  const workspaceId = Auth.getWorkspaceId();
  const userId = Auth.getUser()?.id;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API_URL}/client-success-factors/client-success-factors`, {
          params: { workspaceId, userId }
        });
        if (data && !data.error) {
          setId(data.id || null);
          // Normalize clientChurnTriggers to match options exactly
          const churnTrigger = normalizeOptionValue(data.client_churn_triggers, clientChurnTriggersOptions);
          setFields({
            clientChurnTriggers: churnTrigger,
            uniqueValueProposition: data.unique_value_proposition || '',
            idealClientJourney: data.ideal_client_journey || '',
            budgetConstraints: !!data.budget_constraints,
            projectCompletion: !!data.project_completion,
            lackOfPerceivedValue: !!data.lack_of_perceived_value,
            poorCommunication: !!data.poor_communication,
            competitorSwitching: !!data.competitor_switching,
            internalChangesAtClientCompany: !!data.internal_changes_at_client_company,
            seasonalFactors: !!data.seasonal_factors,
            other: !!data.other,
          });
        } else {
          setId(null);
        }
      } catch (err) {
        setId(null);
      } finally {
        setLoading(false);
      }
    };
    if (workspaceId && userId) fetchData();
    // eslint-disable-next-line
  }, [workspaceId, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e) => {
    const { name, checked } = e.target;
    setFields(prev => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        workspaceId,
        userId,
        clientChurnTriggers: fields.clientChurnTriggers,
        uniqueValueProposition: fields.uniqueValueProposition,
        idealClientJourney: fields.idealClientJourney,
        budgetConstraints: fields.budgetConstraints,
        projectCompletion: fields.projectCompletion,
        lackOfPerceivedValue: fields.lackOfPerceivedValue,
        poorCommunication: fields.poorCommunication,
        competitorSwitching: fields.competitorSwitching,
        internalChangesAtClientCompany: fields.internalChangesAtClientCompany,
        seasonalFactors: fields.seasonalFactors,
        other: fields.other,
      };
      if (id) body.id = id;
      const { data } = await axios.put(`${API_URL}/client-success-factors/client-success-factors`, body);
      if (data && !data.error) {
        toast.success('Client success factors updated!');
        if (data.id) setId(data.id);
      } else {
        toast.error(data.error || 'Error updating client success factors');
      }
    } catch (err) {
      toast.error('Error updating client success factors');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.profileSection}>
      <form onSubmit={handleSubmit}>
        <div className={styles.sectionHeader}>
          <h2>Client Success Factors</h2>
        </div>
        <div className={styles.infoText}>
          <p>
            Understanding what causes clients to leave in your specific industry and what success looks like for
            them enables your AI agent to spot early warning signs and opportunities unique to your business.
            This transforms ClientBloom from a generic tracking tool into a strategic advisor that recognizes
            the precise patterns and triggers that matter most for your client relationships.
          </p>
        </div>
        <div className={`${styles.formGroup} ${styles.configFormGroup}`}>
          <label>Who Manages Client Relationships?</label>
          <select
            className={`${styles.formControl} ${styles.configSelect}`}
            name="clientChurnTriggers"
            value={fields.clientChurnTriggers}
            onChange={handleChange}
          >
            {clientChurnTriggersOptions.map(opt => (
              <option key={opt} value={opt}>{opt || 'Pick Who Manages Client Relationships?'}</option>
            ))}
          </select>
        </div>
        <div className={`${styles.formGroup} ${styles.configFormGroup}`}>
          <label className={styles.churnLabel}>
            What Generally Triggers Client Churn in Your Industry? <span className={styles.normalText}>(Check All That Apply)</span>
          </label>
          <div className={styles.churnCheckboxes}>
            <div className={styles.churnColumn}>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="budgetConstraints" name="budgetConstraints" checked={fields.budgetConstraints} onChange={handleCheckbox} />
                <label htmlFor="budgetConstraints">Budget constraints</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="projectCompletion" name="projectCompletion" checked={fields.projectCompletion} onChange={handleCheckbox} />
                <label htmlFor="projectCompletion">Project completion</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="lackOfPerceivedValue" name="lackOfPerceivedValue" checked={fields.lackOfPerceivedValue} onChange={handleCheckbox} />
                <label htmlFor="lackOfPerceivedValue">Lack of perceived value</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="poorCommunication" name="poorCommunication" checked={fields.poorCommunication} onChange={handleCheckbox} />
                <label htmlFor="poorCommunication">Poor communication</label>
              </div>
            </div>
            <div className={styles.churnColumn}>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="competitorSwitching" name="competitorSwitching" checked={fields.competitorSwitching} onChange={handleCheckbox} />
                <label htmlFor="competitorSwitching">Competitor switching</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="internalChangesAtClientCompany" name="internalChangesAtClientCompany" checked={fields.internalChangesAtClientCompany} onChange={handleCheckbox} />
                <label htmlFor="internalChangesAtClientCompany">Internal changes at client company</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="seasonalFactors" name="seasonalFactors" checked={fields.seasonalFactors} onChange={handleCheckbox} />
                <label htmlFor="seasonalFactors">Seasonal factors</label>
              </div>
              <div className={styles.checkboxSetting}>
                <input type="checkbox" id="other" name="other" checked={fields.other} onChange={handleCheckbox} />
                <label htmlFor="other">Other</label>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label>Unique Value Proposition (Max 200 words)</label>
          <textarea
            name="uniqueValueProposition"
            value={fields.uniqueValueProposition}
            onChange={handleChange}
            placeholder="Describe what makes your service unique and why clients choose you over competitors. Be specific about the transformation or results you provide."
            className={`${styles.formControl} ${styles.fixedHeightInput}`}
            rows="4"
          />
        </div>
        <div className={styles.formGroup}>
          <label>Ideal Client Journey (Max 200 words)</label>
          <textarea
            name="idealClientJourney"
            value={fields.idealClientJourney}
            onChange={handleChange}
            placeholder="Describe the ideal journey from when a client first engages with you through to becoming a long-term advocate. Include key milestones and success markers."
            className={`${styles.formControl} ${styles.fixedHeightInput}`}
            rows="4"
          />
        </div>
        <div className={styles.updateSection}>
          <button className={styles.updateBtn} type="submit" disabled={loading}>{loading ? 'Saving...' : 'Update Information'}</button>
        </div>
      </form>
    </div>
  );
};

export default ClientSuccessFactors; 